import { z } from "zod";
import { ROLES } from "./roles.js";
/**
 * Appointment lifecycle.
 *
 *   requested ──accept──▶ confirmed ──complete──▶ completed ──report──▶ reported
 *       │                     │
 *       └──────cancel─────────┴──▶ cancelled ──reschedule (coordinator)──▶ confirmed
 *
 * The edge back out of `cancelled` is the one that matters commercially: when
 * a specialist drops out, a coordinator rebooking the patient is the service
 * ReassureMed actually sells. It is deliberately restricted to admins.
 */
export const APPOINTMENT_STATUSES = [
    "requested",
    "confirmed",
    "completed",
    "reported",
    "cancelled",
];
export const appointmentStatusSchema = z.enum(APPOINTMENT_STATUSES);
/** The actions that move an appointment between states. */
export const APPOINTMENT_ACTIONS = [
    "accept",
    "cancel",
    "complete",
    "report",
    "reinstate",
];
/**
 * Expressed as data rather than branching code so the whole policy can be read
 * in one place, and so a test can enumerate every (action, status, role) triple
 * rather than only the ones someone remembered to write a case for.
 */
export const TRANSITIONS = {
    accept: [{ from: "requested", to: "confirmed", roles: ["clinician", "admin"] }],
    cancel: [
        { from: "requested", to: "cancelled", roles: ["patient", "clinician", "admin"] },
        { from: "confirmed", to: "cancelled", roles: ["patient", "clinician", "admin"] },
    ],
    complete: [{ from: "confirmed", to: "completed", roles: ["clinician", "admin"] }],
    report: [{ from: "completed", to: "reported", roles: ["clinician", "admin"] }],
    // Only a coordinator can bring a cancelled appointment back to life, because
    // doing so means they have already re-agreed a time with both sides.
    reinstate: [{ from: "cancelled", to: "confirmed", roles: ["admin"] }],
};
/**
 * Is this principal allowed to perform this action on this appointment?
 *
 * Deny order is deliberate. Participation is checked first so that a caller
 * who has nothing to do with an appointment learns only that they are not a
 * participant - never which status it is in or who could act on it. Leaking
 * "wrong status" to a stranger confirms the appointment exists.
 */
export function canTransition(action, appointment, principal) {
    const rules = TRANSITIONS[action];
    if (!isParticipant(appointment, principal)) {
        return { ok: false, reason: "not_a_participant" };
    }
    const forStatus = rules.filter((r) => r.from === appointment.status);
    if (forStatus.length === 0) {
        return {
            ok: false,
            reason: "wrong_status",
            allowedFrom: rules.map((r) => r.from),
        };
    }
    const permitted = forStatus.find((r) => r.roles.some((role) => principal.roles.includes(role)));
    if (!permitted) {
        return {
            ok: false,
            reason: "role_not_permitted",
            allowedRoles: [...new Set(forStatus.flatMap((r) => r.roles))],
        };
    }
    return { ok: true, next: permitted.to };
}
/**
 * Admins are participants in everything - coordinating on behalf of both sides
 * is the job. Every such access is written to the audit table by the caller,
 * which is what keeps that power accountable rather than invisible.
 */
export function isParticipant(appointment, principal) {
    if (principal.roles.includes("admin"))
        return true;
    return (appointment.patient_id === principal.userId || appointment.clinician_id === principal.userId);
}
/**
 * Whether a principal may move an appointment to a different time.
 *
 * A patient or specialist changing a time that the other side already agreed
 * to invalidates that agreement, so the appointment drops back to `requested`
 * and has to be accepted again. A coordinator rescheduling has, by definition,
 * already squared it with both parties out of band - so their change keeps the
 * confirmation. That asymmetry is the product, not a shortcut.
 */
export function rescheduleOutcome(appointment, principal) {
    if (!isParticipant(appointment, principal)) {
        return { ok: false, reason: "not_a_participant" };
    }
    const reschedulable = ["requested", "confirmed"];
    if (!reschedulable.includes(appointment.status)) {
        return { ok: false, reason: "wrong_status", allowedFrom: reschedulable };
    }
    if (principal.roles.includes("admin")) {
        return { ok: true, next: appointment.status };
    }
    return { ok: true, next: "requested" };
}
/** Statuses whose appointments still need someone to do something. */
export const OPEN_STATUSES = ["requested", "confirmed", "completed"];
export function isOpen(status) {
    return OPEN_STATUSES.includes(status);
}
/** Exported for exhaustiveness tests. */
export const ALL_ROLES = ROLES;
//# sourceMappingURL=appointment.js.map